import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle, Send, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DirectLeadPage() {
    const { orgCode } = useParams();
    const [org, setOrg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        lead_name: '',
        lead_email: '',
        lead_phone: '',
        lead_company: '',
        lead_street: '',
        lead_zip: '',
        lead_city: '',
        lead_notes: ''
    });

    useEffect(() => {
        const fetchOrg = async () => {
            try {
                const response = await axios.get(`${API}/api/organizations/${orgCode}/public`);
                setOrg(response.data);
            } catch (error) {
                toast.error('Organisation nicht gefunden');
            } finally {
                setLoading(false);
            }
        };
        fetchOrg();
    }, [orgCode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.lead_name.trim()) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }
        
        if (!formData.lead_email && !formData.lead_phone) {
            toast.error('Bitte geben Sie eine E-Mail oder Telefonnummer ein');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API}/api/organizations/${orgCode}/leads`, formData);
            setSubmitted(true);
            toast.success('Vielen Dank! Ihre Anfrage wurde übermittelt.');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Senden');
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (!org) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <Card className="w-full max-w-md mx-4">
                    <CardContent className="pt-6 text-center">
                        <p className="text-red-600 font-medium">Organisation nicht gefunden</p>
                        <p className="text-slate-500 text-sm mt-2">
                            Der Organisationscode "{orgCode}" existiert nicht.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const primaryColor = org.primary_color || '#3b82f6';

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: `${primaryColor}10` }}>
                <Card className="w-full max-w-md">
                    <CardContent className="pt-8 pb-8 text-center">
                        <div 
                            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                            style={{ backgroundColor: `${primaryColor}20` }}
                        >
                            <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
                        </div>
                        <h2 className="text-xl font-semibold text-slate-800 mb-2">
                            Vielen Dank!
                        </h2>
                        <p className="text-slate-600">
                            Ihre Anfrage wurde erfolgreich übermittelt. 
                            Wir werden uns in Kürze bei Ihnen melden.
                        </p>
                        {org.name && (
                            <p className="text-sm text-slate-500 mt-4">
                                - {org.name}
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-8 px-4" style={{ backgroundColor: `${primaryColor}10` }}>
            <div className="max-w-lg mx-auto">
                {/* Header */}
                <div className="text-center mb-6">
                    {org.logo && (
                        <img 
                            src={org.logo.startsWith('http') ? org.logo : `${API}${org.logo}`}
                            alt={org.name}
                            className="h-16 mx-auto mb-4 object-contain"
                        />
                    )}
                    <h1 className="text-2xl font-bold text-slate-800">
                        {org.name || 'Kontaktformular'}
                    </h1>
                    <p className="text-slate-600 mt-2">
                        Hinterlassen Sie Ihre Kontaktdaten und wir melden uns bei Ihnen.
                    </p>
                </div>

                {/* Form */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Ihre Kontaktdaten</CardTitle>
                        <CardDescription>
                            Felder mit * sind Pflichtfelder
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <Label htmlFor="lead_name">Name *</Label>
                                <Input
                                    id="lead_name"
                                    name="lead_name"
                                    value={formData.lead_name}
                                    onChange={handleChange}
                                    placeholder="Ihr vollständiger Name"
                                    required
                                    data-testid="direct-lead-name"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="lead_email">E-Mail</Label>
                                    <Input
                                        id="lead_email"
                                        name="lead_email"
                                        type="email"
                                        value={formData.lead_email}
                                        onChange={handleChange}
                                        placeholder="ihre@email.de"
                                        data-testid="direct-lead-email"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="lead_phone">Telefon</Label>
                                    <Input
                                        id="lead_phone"
                                        name="lead_phone"
                                        type="tel"
                                        value={formData.lead_phone}
                                        onChange={handleChange}
                                        placeholder="+49 123 456789"
                                        data-testid="direct-lead-phone"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="lead_company">Firma (optional)</Label>
                                <Input
                                    id="lead_company"
                                    name="lead_company"
                                    value={formData.lead_company}
                                    onChange={handleChange}
                                    placeholder="Firmenname"
                                    data-testid="direct-lead-company"
                                />
                            </div>

                            <div>
                                <Label htmlFor="lead_street">Straße (optional)</Label>
                                <Input
                                    id="lead_street"
                                    name="lead_street"
                                    value={formData.lead_street}
                                    onChange={handleChange}
                                    placeholder="Straße und Hausnummer"
                                    data-testid="direct-lead-street"
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <Label htmlFor="lead_zip">PLZ</Label>
                                    <Input
                                        id="lead_zip"
                                        name="lead_zip"
                                        value={formData.lead_zip}
                                        onChange={handleChange}
                                        placeholder="12345"
                                        data-testid="direct-lead-zip"
                                    />
                                </div>
                                <div className="col-span-2">
                                    <Label htmlFor="lead_city">Ort</Label>
                                    <Input
                                        id="lead_city"
                                        name="lead_city"
                                        value={formData.lead_city}
                                        onChange={handleChange}
                                        placeholder="Stadt"
                                        data-testid="direct-lead-city"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="lead_notes">Nachricht (optional)</Label>
                                <Textarea
                                    id="lead_notes"
                                    name="lead_notes"
                                    value={formData.lead_notes}
                                    onChange={handleChange}
                                    placeholder="Ihre Nachricht oder Anmerkungen..."
                                    rows={3}
                                    data-testid="direct-lead-notes"
                                />
                            </div>

                            <Button 
                                type="submit" 
                                className="w-full"
                                disabled={submitting}
                                style={{ backgroundColor: primaryColor }}
                                data-testid="direct-lead-submit"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Wird gesendet...
                                    </>
                                ) : (
                                    <>
                                        <Send className="mr-2 h-4 w-4" />
                                        Anfrage senden
                                    </>
                                )}
                            </Button>

                            <p className="text-xs text-center text-slate-500 mt-4">
                                Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten zu.
                            </p>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
